
import { Handshake } from "lucide-react";
import OptimizedImage from "@/components/ui/optimized-image";

interface Partner {
  name: string;
  logo: string;
}

interface PartnersCarouselProps {
  altTitle?: string;
  language?: 'fi' | 'en' | 'sv' | 'ar' | 'he';
}

const translations = {
  fi: {
    title: "Kumppaneitamme",
    description: "Yhteistyössä Suomen johtavien rahoituslaitosten kanssa tarjoamme sinulle kattavan valikoiman lainatuotteita kilpailukykyisin ehdoin.",
    footer: "Tarjoamme yhden hakemuksen kautta pääsyn useiden pankkien ja rahoituslaitosten lainatuotteisiin."
  },
  en: {
    title: "Our Partners",
    description: "In cooperation with Finland's leading financial institutions, we offer you a comprehensive range of loan products at competitive terms.",
    footer: "We provide access to loan products from multiple banks and financial institutions through a single application."
  },
  sv: {
    title: "Våra Partners",
    description: "I samarbete med Finlands ledande finansinstitut erbjuder vi dig ett omfattande utbud av låneprodukter med konkurrenskraftiga villkor.",
    footer: "Vi ger tillgång till låneprodukter från flera banker och finansinstitut genom en enda ansökan."
  },
  ar: {
    title: "شركاؤنا",
    description: "بالتعاون مع المؤسسات المالية الرائدة في فنلندا، نقدم لك مجموعة شاملة من منتجات القروض بشروط تنافسية.",
    footer: "نوفر الوصول إلى منتجات القروض من بنوك ومؤسسات مالية متعددة من خلال طلب واحد."
  },
  he: {
    title: "השותפים שלנו",
    description: "בשיתוף פעולה עם המוסדות הפיננסיים המובילים בפינלנד, אנו מציעים לך מגוון מקיף של מוצרי הלוואה בתנאים תחרותיים.",
    footer: "אנו מספקים גישה למוצרי הלוואה מבנקים ומוסדות פיננסיים מרובים באמצעות בקשה אחת."
  }
};

const PartnersCarousel = ({ altTitle, language = 'fi' }: PartnersCarouselProps) => {
  const partners: Partner[] = [
    { name: "Avida", logo: "/lovable-uploads/a462af2b-df5b-470c-914d-a29f1c30a791.png" },
    { name: "Svea", logo: "/lovable-uploads/5830f45f-5c08-4e8c-90f2-598da9e5d6f1.png" },
    { name: "Aktia", logo: "/lovable-uploads/257902ff-1621-4cee-bd74-34dded1b9cf7.png" },
    { name: "TFBank", logo: "/lovable-uploads/9d6c3832-6c20-4ba2-b15f-afa8462a40fe.png" },
    { name: "Alisa Pankki", logo: "/lovable-uploads/76d0337a-c3da-430f-aab0-427bc85d0e2c.png" },
    // Removed Credigo logo which was between Alisa Pankki and Brocc
    { name: "Brocc", logo: "/lovable-uploads/06aa47f4-13db-47c0-b025-64280248c49c.png" },
    { name: "Lea Bank", logo: "/lovable-uploads/066e48f0-e60b-4bd4-a854-c19c456bc8c2.png" },
    { name: "Vippi Saldo", logo: "/lovable-uploads/5c9cc14a-bba9-4726-b635-e77125a9336c.png" },
    { name: "Northmill Bank", logo: "/lovable-uploads/18cdc484-3009-46bc-a563-b7d468c00492.png" },
    { name: "GF Money", logo: "/lovable-uploads/862c1905-947c-4c87-b798-c2dd173e091a.png" },
    { name: "Saldo Bank", logo: "/lovable-uploads/bc351ffe-9b9d-49c7-8b76-30a9278c7a99.png" },
    { name: "Halino", logo: "/lovable-uploads/be54a3f1-9446-4abb-8fa7-3c360ce0b025.png" },
    { name: "Bondora", logo: "/lovable-uploads/bd586863-42aa-41f2-98cd-e0b7c69bf776.png" },
    // Removed Morrow logo from the list
    { name: "Fixura", logo: "/lovable-uploads/dfc77703-4815-49f3-be01-80f8e94eb9f0.png" },
    { name: "Ferratum", logo: "/lovable-uploads/b9bbaa82-8bbb-49d3-8cf7-27aaf3784296.png" },
    { name: "Instabank", logo: "/lovable-uploads/637c4c39-2514-4d64-b4b8-95099381e901.png" },
    { name: "POP Pankki", logo: "/lovable-uploads/1c6bb4d7-ec75-4762-9448-b3876574bd52.png" },
    { name: "Nordax Bank", logo: "/lovable-uploads/571b9710-ee14-4ce3-89ab-3628fe063b18.png" },
    { name: "Bank Norwegian", logo: "/lovable-uploads/93269ad7-92d5-40e1-a9a3-93dd2e9b1eac.png" },
    { name: "SweepBank", logo: "/lovable-uploads/36ba7093-3deb-443f-9f9c-c6ccda209996.png" },
  ];

  const isRtl = language === 'ar' || language === 'he';
  const content = translations[language] || translations.fi;

  return (
    <section className="py-12 bg-gray-50" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-8">
          <Handshake className="h-6 w-6 text-blue-600" />
          <h2 className="text-3xl font-bold text-center text-blue-700">
            {altTitle || content.title}
          </h2>
        </div>
        
        <p className="text-center text-gray-600 max-w-2xl mx-auto mb-10">
          {content.description}
        </p>
        
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
            {partners.map((partner, index) => (
              <div 
                key={index} 
                className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow flex items-center justify-center text-center border border-gray-100"
                style={{ height: "100px" }}
              >
                <div className="flex items-center justify-center h-full w-full relative p-2">
                  {/* Added background styling to normalize appearance */}
                  <div className="absolute inset-0 bg-white rounded-md flex items-center justify-center">
                    <OptimizedImage 
                      src={partner.logo}
                      alt={`${partner.name} logo`}
                      className="max-h-[80%] max-w-[80%]"
                      objectFit="contain"
                      loading="lazy"
                      quality={75}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="text-center mt-8">
          <p className="text-sm text-gray-500">
            {content.footer}
          </p>
        </div>
      </div>
    </section>
  );
};

export default PartnersCarousel;
